import { action, computed, makeObservable, observable } from 'mobx'
import dayjs from 'dayjs'
import { Subtask, Subtasks } from '../index'
import { Sprints } from './sprints-ids'

/**
 * Class representing a TimedSubtask, extending Subtask.
 */
/**
 * TimedSubtask extends Subtask to include time tracking and related sprint IDs.
 *
 * @class TimedSubtask
 * @extends Subtask
 * @param {Object} params - Initialization parameters.
 * @param {number|string} params.name - Unix timestamp (in seconds or milliseconds) or date string for this subtask.
 * @param {number} [params.hours=0] - Number of hours allocated.
 * @param {Object} params.sprints - Raw sprint IDs structure.
 * @param {...any} subtaskData - All other Subtask constructor parameters.
 */
class TimedSubtask extends Subtask {
	hours

	constructor({ name, hours = 0, sprints, ...subtaskData }) {
		super({ ...subtaskData, name: name }) // Convert 'name' to dayjs object
		this.hours = hours

		this.sprints = new Sprints(sprints)

		makeObservable(this, {
			hours: observable,
			sprints: observable,
			sprintIds: computed,
			setSprintsIds: action,
			addSprintsId: action.bound,
			deleteSprintsId: action.bound
		})
	}

	/**
	 * Computed list of sprint IDs associated with this subtask.
	 * @type {Array<string>}
	 */
	get sprintIds() {
		return this.sprints.list
	}

	/**
	 * Replaces the entire list of sprint IDs.
	 * @param {Array<string>} newIds - New sprint IDs array.
	 */
	setSprintsIds(newIds) {
		this.sprints.replaceSprintIds(newIds)
	}

	/**
	 * Computed display name for this timed subtask.
	 * Formats the internal `name` as a date and appends hours.
	 * @type {string}
	 */
	get displayName() {
		let displayName = dayjs.unix(this.name)
		displayName = displayName.isValid()
			? displayName.format('YYYY-MM-DD')
			: dayjs(this.name).format('YYYY-MM-DD')

		return `${displayName} (${this.hours}hrs)`
	}

	/**
	 * Adds a single sprint ID to the collection.
	 * @param {string} id - Sprint ID to add.
	 */
	addSprintsId(id) {
		this.sprints.addSprint(id)
	}

	/**
	 * Removes a single sprint ID from the collection.
	 * @param {string} id - Sprint ID to remove.
	 */
	deleteSprintsId(id) {
		this.sprints.removeSprint(id)
	}

	// You can override or add specific methods from Subtask if needed
	/**
	 * Overrides base type to indicate this is a timed subtask.
	 * @type {string}
	 */
	get subtaskType() {
		return 'timed'
	}
}

/**
 * Class representing a collection of TimedSubtasks, extending Subtasks.
 * Contains additional properties and methods specific to TimedSubtasks.
 * @extends {Subtasks}
 * @class TimedSubtasks
 */
class TimedSubtasks extends Subtasks {
	constructor(list = []) {
		super(list)
		makeObservable(this)
	}

	get usedItemClass() {
		return TimedSubtask
	}

	addSubtask(
		name,
		hours,
		sprints,
		images,
		description,
		status,
		web,
		sortId,
		createdBy
	) {
		const subtask = new TimedSubtask({
			name,
			hours,
			sprints,
			images,
			description,
			status,
			web,
			sortId,
			createdBy
		})
		this.list.push(subtask)
	}

	// You can override or extend methods from Subtasks if needed
	/**
	 * Always returns 'timed' for this collection.
	 * @type {string}
	 */
	get subtaskType() {
		return 'timed'
	}

	/**
	 * Prepares a plain array of timed-subtasks for saving.
	 * Preserves comments and sprint IDs arrays.
	 * @returns {Array<Object>}
	 */
	prepareForSave() {
		if (!Array.isArray(this.list)) {
			return [] // Ensure the function returns an empty array if this.list is not an array
		}

		return this.list.map((subtask) => {
			// Ensure subtask.comments.list is an array before mapping
			const comments = Array.isArray(subtask.comments.list)
				? subtask.comments.list.map((comment) => comment)
				: [] // Return an empty array if subtask.comments.list is not an array

			// Ensure subtask.sprints.list is an array before mapping
			const sprintsIds = Array.isArray(subtask.sprints.list)
				? subtask.sprints.list.map((sprint) => sprint)
				: [] // Return an empty array if subtask.sprints.list is not an array

			return {
				...subtask,
				comments,
				sprintsIds
			}
		})
	}
}

export { TimedSubtask, TimedSubtasks }
